import { EntityDetailsDisplayInfo } from '../components/EntityDetails/EntityDetailsDisplayInfo';
import { Aircraft, AircraftWbStation } from './aircraft';
import AirctaftSarEquipment from './aircraftSar';
import { Crew } from './crew';
import { Passenger } from './passenger';
import { Route, RoutePoint, calculateDistanceBetweenRoutePoints } from './route';
import { store } from '../store/store'
import { FlightPlanPoint, FlightPlanZZZZPoint, getRoutePointFromFlightPlanPoint, getRoutePointFromFlightPlanZZZZPoint } from './flightPlan';
import { Organization } from './organization';
import { AirfieldSlotType } from './airfieldSlots';

type Flight = {
    id?: string;

    aircraft: Aircraft;
    route?: Route
    customRoute?: {
        departure: FlightPlanPoint,
        destination: FlightPlanPoint,
        waypoints: FlightPlanZZZZPoint[],
        intermediateStops: RoutePoint[],
        aftnAddresses?: string[],
    }
    pic: Crew
    sic?: Crew
    deadheadCrew?: Crew,

    studentPilot?: Crew
    passengers: Passenger[]

    soloSupervisor?: Crew //only for training and risk assessment

    flightId: string,
    flightIdType: "reg" | "aircraft" | "crew" | "custom",
    
    passengersLeg?: FlightPassengerLeg
    //Per-passenger boarding/leaving stop, as indices into [departure, ...intermediateStops, destination].
    //A passenger without an entry flies the passengersLeg span (or the whole trip when that isn't set).
    passengerLegs?: { [passengerId: string]: FlightPassengerStops }

    altn1?: string;
    altn2?: string;
    flightRules: "V" | "I" | "Y" | "Z";
    flightType: "S" | "N" | "G" | "M" | "X";
    cruiseSpeed: {unit: "N" | "K" , value: number};
    cruiseLevel: {unit: "VFR" | "F" | "A" | "S" | "M", value: number};
    departureDateTime: Date;
    //Off-block time currently filed with ATC — set by a DLA, so it can differ from departureDateTime
    //(the operator's schedule, which a delay does not move). Read-only from the client's point of view.
    currentEobt?: Date;
    flightCategory: "AOC" | "Training" | "Other"
    trainingFlightCategory?: "Dual" | "Solo" | "Checkride"
    
    equipmentCodes: string,
    surveillanceEquipmentCodes: string
    personsOnBoard: number,
    field18Remarks: string,
    sarEquipment: AirctaftSarEquipment,
    operatorName?: string

    //IFR / IFPS (only relevant when flightRules !== "V")
    pbn?: string,
    field18Nav?: string,
    field18Com?: string,
    field18Dat?: string,
    ifpsRoute?: string, //validated Field 15 route from route-find
    ifrFuelPerHour?: number, //fuel burn per hour, in the aircraft's fuel unit
    ifrMaxMass?: number, //maximum take-off mass (MTOW), in kg
    ifrDistanceNM?: number, //autorouter-computed route distance; drives the on-the-fly EET for IFR
    ifrValidation?: IfrValidation, //last IFPUV pre-validation — part of the file status for non-VFR plans

    totalEETMins: number, //minutes
    enduranceMins: number, //minutes

    isCostSharing?: boolean
    
    // extraAftnAddresses: string[]
    
    fuelOnBoard: FlightFuelOnBoard[]
    
    wb?: FlightWb

    briefing?: FlightBriefing
    fplFile: FlightFplFile

    walletPassExternalId?: string
    checkIns?: { [key: string]: {
        time: Date,
        checkedInByUid: string
    } }

    briefingPilotAccept?: FlightBriefingPilotAccept

    //Last-minute changes after the briefing pack was generated (set by the backend revision endpoints)
    noShows?: { [passengerId: string]: { time: Date, byUid: string } }
    wbRevisions?: FlightWbRevision[]
    trainingRecordRequestedAt?: Date //server-managed: when the instructor was emailed

    postFlightData?: FlightPostFlightData

    customDocs?: FlightCustomDocs

    airfieldSlots?:{
        departure?: FlightAirfieldSlot
    }
}

export default Flight;

export type FlightPassengerLeg = {
    route: {
        from: RoutePoint
        fromIndex: number
        to: RoutePoint
        toIndex: number
    }

    boardingTime?: Date
    delayAtFrom?: number
    delayAtTo?: number
    changePaxAtFrom?: number
    changePaxAtTo?: number
    //Per-passenger mode (passengerLegs set): details per stop, indexed like the stop points
    //[departure, ...intermediateStops, destination]. Drives the Field 18 RMK and the boarding time
    //printed on the pass of everyone boarding at that stop.
    stopDetails?: (FlightPassengerStopDetail | null)[]
}

export type FlightPassengerStopDetail = {
    boardingTime?: string //ISO date-time (UTC)
    standbyMins?: number
}

export type FlightFuelOnBoard = {
    station: AircraftWbStation
    fuel: number
    mass: number
}

export type FlightPassengerStops = {
    fromIndex: number
    toIndex: number
}

export type FlightWb = {
    status: "not-calculated" | "ok" | "error" | "no-airctaft-data",
    loads: FlightWbLoad[]  //crew, passengers, baggage, other — leg 1 when the flight has stops
    errors?: string[]
    //One entry per leg, only for flights with intermediate stops. loads mirrors legs[0].loads and
    //status is the worst leg's status.
    legs?: FlightWbLeg[]
}

export type FlightWbLeg = {
    fromIndex: number //index into [departure, ...intermediateStops, destination]
    toIndex: number
    loads: FlightWbLoad[]
    fuelOnBoard: FlightFuelOnBoard[] //departure fuel of this leg (leg 1 = flight.fuelOnBoard)
    fuelCarried?: boolean //no uplift at the stop: departs with the previous leg's landing fuel, so it follows changes to earlier legs
    eetMins: number
    status: "ok" | "error"
    errors?: string[]
}

export type FlightWbLoad = {
    station: AircraftWbStation
    mass: number
    name: string
    //'other' = non-person load strapped on a seat (cargo/ballast) — excluded from any pax
    //counting logic; absent/'person' = occupant from crew/passenger records
    loadType?: "person" | "other"
    //the crew/passenger record this load came from — lets legs and no-shows find a person's seat
    personId?: string
    personKind?: "crew" | "passenger"
}

export type FlightWbRevision = {
    n: number
    time: Date
    byUid: string
    changes: FlightWbRevisionChanges
    wb: FlightWb
    pdfPages: number
    signed?: { time: Date }
}

export type FlightWbRevisionChanges = {
    fuel?: { legIndex: number, before: number, after: number }[] //total mass per leg, aircraft massUnit
    noShows?: string[] //passenger ids
    baggage?: { legIndex: number, stationId: string, before: number, after: number }[]
    seating?: boolean
}

export type FlightBriefingPilotAccept = {
    signatureBase64: string
    time: Date
}

export type FlightBriefing = 
    FlightBriefingError |
    FlightBriefingNotStarted |
    FlightBriefingScheduled |
    FlightBriefingInQueue |
    FlightBriefingInProgress |
    FlightBriefingReady

type FlightBriefingError = {
    status: "error",
    error: string
}

type FlightBriefingNotStarted = {
    status: "not-started",
}

type FlightBriefingScheduled = {
    status: "scheduled",
    taskId: string
    scheduleTime: Date
}

type FlightBriefingInQueue = {
    status: "in-queue",
    routeId: string
}

type FlightBriefingInProgress = {
    status: "in-progress",
    routeId: string,
    briefingId: string
}

type FlightBriefingReady = {
    status: "ready",
    routeId: string,
    briefingId: string
}

export type FlightFplFile = 
    FlightFplFileError |
    FlightFplFileCancelled |
    FlightFplFileNotFiled |
    FlightFplFileReadyToFile |
    FlightFplFileScheduled |
    FlightFplFileFiled |
    FlightFplFileDelayed


type FlightFplFileError = {
    status: "error",
    error: string,
}

//A CNL that reached ATC — a successful outcome, not a failure. The plan is finished; to fly again the
//user duplicates the flight, which starts a clean FPL.
type FlightFplFileCancelled = {
    status: "cancelled",
    cancelledAt: Date,
    fplCode: string, // the FPL that was cancelled — kept so the plan still shows what ATC was told
}

type FlightFplFileNotFiled = {
    status: "not-filed",
    fplCode: string
}

type FlightFplFileReadyToFile = {
    status: "ready-to-file",
    fplCode: string
}

type FlightFplFileScheduled = {
    status: "scheduled",
    taskId: string,
    scheduleTime: Date
    fplCode: string
    aftnAddresses: string[]
}

type FlightFplFileFiled = {
    status: "filed",
    fplCode: string
    fillingTime: Date
    fillingTimeStr: string
    ifps?: FplIfps
}

export type FplIfpsState = "filed" | "queued" | "acked" | "rejected" | "suspended"

export type FplIfpsOrm = {
    type: string
    receivedAt: string
    ifplId?: string
    errors?: string[]
    suggestedRoute?: string
    ctot?: string
    isTest?: boolean
    raw?: string //full raw ADEXP telegram as received
}

export type FplIfps = {
    state: FplIfpsState
    ifplId?: string
    lastOrmType?: string
    lastUpdatedAt?: string //ISO
    ctot?: string //HHMM calculated take-off time (ATFM slot)
    regulations?: string[]
    errors?: string[] //from REJ/FLS
    suggestedRoute?: string //POSRTE (REJ) / NEWRTE (FLS)
    ormHistory: FplIfpsOrm[]
}

export function isIfrFlightRules(flightRules: Flight["flightRules"]): boolean {
    return flightRules !== "V"
}

// Stored IFPUV pre-validation result for a non-VFR FPL. `fplCode` is the exact FPL string it was produced
// for, so a stale result (FPL since changed) can be detected. Part of the file status (badge + filing gate).
export type IfrValidation = {
    result: "ok" | "errors"
    validation: string
    errors: string[]
    fplCode: string
    at: string
}

// A stored validation only counts when it was produced for the CURRENT FPL string and passed (no errors).
export function isIfrValidationCurrentAndOk(validation: IfrValidation | undefined, currentFplCode: string | undefined): boolean {
    return !!validation && !!currentFplCode && validation.fplCode === currentFplCode && validation.result === "ok"
}

export function getIfpsStateBadge(state: FplIfpsState): { label: string; bg: string; text?: "dark" | "light"; style?: { backgroundColor: string } } {
    switch (state) {
        case "filed":
            return { label: "Filed — awaiting IFPS", bg: "info", text: "dark" }
        case "queued":
            return { label: "Manual processing (MAN)", bg: "warning", text: "dark" }
        case "acked":
            return { label: "Accepted (ACK)", bg: "success" }
        case "rejected":
            return { label: "Rejected (REJ)", bg: "danger" }
        case "suspended":
            return { label: "Suspended (FLS)", bg: "warning", text: "light", style: { backgroundColor: "#fd7e14" } }
    }
}

// CTOT arrives from IFPS as bare HHMM. Always render it through here so the badge, the message list and
// the pilot's slot callout can never show the same slot three different ways. Non-HHMM is passed through.
export function formatCtot(ctot: string): string {
    return /^\d{4}$/.test(ctot) ? `${ctot.slice(0, 2)}:${ctot.slice(2)}Z` : ctot
}

// One-line plain-language gist of a single ORM, used as the collapsed headline in the IFPS message list
// and mirrored by the IFPS notification email so the wording a pilot reads in the inbox matches the app.
export function ifpsOrmSummary(orm: FplIfpsOrm): string {
    const meaning: { [k: string]: string } = {
        ACK: "Accepted",
        MAN: "Manual processing",
        REJ: "Rejected",
        FLS: "Suspended",
        SAM: "Slot allocated",
        SRM: "Slot revised",
        SLC: "Slot cancelled",
        DES: "De-suspended",
    };
    const bits: string[] = [];
    if (meaning[orm.type]) bits.push(meaning[orm.type]);
    if (orm.ctot) bits.push(`CTOT ${formatCtot(orm.ctot)}`);
    if (orm.errors && orm.errors.length > 0) bits.push(orm.errors[0]);
    if (orm.suggestedRoute) bits.push(`→ ${orm.suggestedRoute}`);
    if (orm.type === "ACK" && orm.ifplId) bits.push(orm.ifplId);
    const text = bits.join(" · ");
    return text.length > 90 ? text.slice(0, 89) + "…" : text;
}

type FlightFplFileDelayed = {
    status: "delayed",
    initialFplCode: string
    initialFillingTime: Date
    initialFillingTimeStr: string

    newTime: Date
    delayedDlaCode: string
    delayedDlaFillingTime: Date
    delayedDlaFillingTimeStr: string
    //IFPS tracking survives a DLA — the plan is still active at EUROCONTROL (same IFPL ID).
    ifps?: FplIfps
}

export type FlightPostFlightData = {
    blockOffFuel?: number
    blockOnFuel?: number
    takeoffFuel?: number
    landingFuel?: number

    blockOffTime?: Date
    blockOnTime?: Date
    takeoffTime?: Date
    landingTime?: Date
}

export type FlightCustomDocsType = "riskAssessment" | "soloSupCertificate" | "trainingRecord"

export type FlightTrainingRecordEvaluation = "aboveAverage" | "average" | "belowAverage" | "unsatisfactory"

export type FlightCustomDocs = {
    riskAssessment?: {
        createdAt: Date
        createdByUid: string
        score: number
        level: FlightRiskAssessmentLevel
        acceptanceStatus: boolean
    },
    riskAssessmentSigned?: {
        signedAt: Date
        signedByUid: string
    },
    soloSupCertificate?: {
        createdAt: Date
        createdByUid: string
    },
    soloSupCertificateSigned?: {
        signedAt: Date
        signedByUid: string
    },
    trainingRecord?: {
        createdAt: Date
        createdByUid: string
        template: "aeroplane" | "helicopter"
        lessonNo: string
        exercises: string[]
        evaluation?: FlightTrainingRecordEvaluation
    },
    trainingRecordSigned?: {
        signedAt: Date
        signedByUid: string
    }
}

export type FlightRiskAssessmentLevel = {
    name: string
    maxScore: number
    requestSignFrom?: {
        name: string
        email: string
    }
}

export type FlightAirfieldSlot = {
    airfieldId: string,
    startDateTimeEpoch: number,
    slotType: AirfieldSlotType,
    bookedSlotKey: string
}

export function getFlightRiskAssessmentLevelForScore(score: number, orgLevels: FlightRiskAssessmentLevel[]): FlightRiskAssessmentLevel {
    for (const level of orgLevels){
        if (score <= level.maxScore){
            return level
        }
    }
    throw new Error("No level found for score " + score)
}

export type FlightRulesMode = "vfr" | "ifr"

export function getFlightDisplayInfo(fuelUnit?: string, rulesMode?: FlightRulesMode): EntityDetailsDisplayInfo<Flight> {
    const info: EntityDetailsDisplayInfo<Flight> = {
        entityName: "Flight",
        entityCanBeDeleted: true,
        sections: [
            {
                sectionName: "Date & Time",
                sectionId: "date",
                shouldDisplay: (flight) => flight.airfieldSlots?.departure === undefined,
                inputs: [
                    {
                        required: true,
                        name: "Departure Date (UTC)",
                        timeName: "Departure Time (UTC)",
                        key: "departureDateTime",
                        type: "dateTime",
                        defaultValue: getDefaultDateForFlightDateTimePicker(),
                        useUtc: true,
                        dateFlatpickrOptions: {
                            dateFormat: "d-m-Y",
                        },
                        timeFlatpickrOptions: {
                            dateFormat: "H:i",
                            time_24hr: true,
                            minuteIncrement: 5,
                        },
                        validateRules: {
                            validate: {
                                inFuture: (value) => {
                                    const now = new Date();
                                    const utcNow = new Date(
                                        now.getUTCFullYear(),
                                        now.getUTCMonth(),
                                        now.getUTCDate(),
                                        now.getUTCHours(),
                                        now.getUTCMinutes()
                                    );

                                    if ((value as Date) < utcNow) {
                                        return "Departure Date and Time must be in the future";
                                    }

                                    return true;
                                }
                            }
                        }
                    }
                ]
            },
            {
                sectionName: "Date & Time (Slot)",
                sectionId: "date",
                shouldDisplay: (flight) => flight.airfieldSlots?.departure !== undefined,
                inputs: [
                    {
                        required: true,
                        disabled: true,
                        name: "Departure Date (UTC)",
                        timeName: "Departure Time (UTC)",
                        key: "departureDateTime",
                        type: "dateTime",
                        defaultValue: getDefaultDateForFlightDateTimePicker(),
                        useUtc: true,
                        dateFlatpickrOptions: {
                            dateFormat: "d-m-Y",
                        },
                        timeFlatpickrOptions: {
                            dateFormat: "H:i",
                            time_24hr: true,
                            minuteIncrement: 5,
                        },
                        validateRules: {
                            validate: {
                                inFuture: (value) => {
                                    const now = new Date();
                                    const utcNow = new Date(
                                        now.getUTCFullYear(),
                                        now.getUTCMonth(),
                                        now.getUTCDate(),
                                        now.getUTCHours(),
                                        now.getUTCMinutes()
                                    );

                                    if ((value as Date) < utcNow) {
                                        return "Departure Date and Time must be in the future";
                                    }

                                    return true;
                                }
                            }
                        }
                    },
                    {
                        required: true,
                        name: "Departure Airfield Slot",
                        key: "airfieldSlots.departure",
                        type: "airfieldSlot",
                    }
                ]
            },
            {
                sectionName: "Flight Category",
                sectionId: "flightCategory",
                shouldDisplay: (flight) => flight.flightCategory !== "Training",
                inputs: [
                    {
                        required: true,
                        name: "Flight Category",
                        key: "flightCategory",
                        type: "dropdown",
                        options: [
                            { value: "AOC", label: "AOC" },
                            { value: "Training", label: "Training" },
                            { value: "Other", label: "Other/Private" },
                        ]
                    }
                ]
            },
            {
                sectionName: "Flight Category",
                sectionId: "flightCategory",
                shouldDisplay: (flight) => flight.flightCategory === "Training",
                inputs: [
                    {
                        required: true,
                        name: "Flight Category",
                        key: "flightCategory",
                        type: "dropdown",
                        options: [
                            { value: "AOC", label: "AOC" },
                            { value: "Training", label: "Training" },
                            { value: "Other", label: "Other/Private" },
                        ]
                    },
                    {
                        required: true,
                        name: "Training Flight Category",
                        key: "trainingFlightCategory",
                        type: "dropdown",
                        options: [
                            { value: "Dual", label: "Dual" },
                            { value: "Solo", label: "Solo" },
                            { value: "Checkride", label: "Checkride" },
                        ]
                    }
                ]
            },
            {
                sectionName: "Aircraft",
                sectionId: "aircraft",
                shouldDisplay: (flight) => flight.flightCategory !== undefined,
                inputs: [
                    {
                        required: true,
                        name: "Aircraft",
                        key: "aircraft",
                        type: "aircraft",
                        parentEntity: "Flight"
                    },
                    {
                        required: true,
                        name: "Flight ID",
                        key: "flightId",
                        flightIdTypeKey: "flightIdType",
                        type: "flightId"
                    }
                ]
            },
            {
                sectionName: "Crew",
                sectionId: "crewTraining",
                shouldDisplay: (flight) => flight.flightCategory === "Training" && flight.trainingFlightCategory === "Dual",
                inputs: [
                    {
                        name: "PIC - Flight Instructor",
                        key: "pic",
                        type: "crew",
                        required: true
                    },
                    {
                        name: "Student Pilot",
                        key: "studentPilot",
                        type: "crew",
                        required: false
                    },
                    {
                        name: "Dead Head Crew",
                        key: "deadheadCrew",
                        type: "crew",
                        required: false
                    }
                ]
            },
            {
                sectionName: "Crew",
                sectionId: "crewTraining",
                shouldDisplay: (flight) => flight.flightCategory === "Training" && flight.trainingFlightCategory === "Solo",
                inputs: [
                    {
                        name: "PIC",
                        key: "pic",
                        type: "crew",
                        required: true
                    },
                    {
                        name: "Solo Supervisor",
                        key: "soloSupervisor",
                        type: "crew",
                        required: (store.getState().organizations.selectedOrganization?.riskAssessment.enabled ?? false)
                    }
                ]
            },
            {
                sectionName: "Crew",
                sectionId: "crewTraining",
                shouldDisplay: (flight) => flight.flightCategory === "Training" && flight.trainingFlightCategory === "Checkride",
                inputs: [
                    {
                        name: "PIC",
                        key: "pic",
                        type: "crew",
                        required: true
                    },
                    {
                        name: "Flight Examiner",
                        key: "sic",
                        type: "crew",
                        required: false
                    },
                    {
                        name: "Dead Head Crew",
                        key: "deadheadCrew",
                        type: "crew",
                        required: false
                    }
                ]
            },

            {
                sectionName: "Crew",
                sectionId: "crewAoc",
                shouldDisplay: (flight) => flight.flightCategory === "AOC",
                inputs: [
                    {
                        name: "PIC",
                        key: "pic",
                        type: "crew",
                        required: true
                    },
                    {
                        name: "SIC",
                        key: "sic",
                        type: "crew",
                    },
                    {
                        name: "Dead Head Crew",
                        key: "deadheadCrew",
                        type: "crew",
                    }
                ]
            },
            {
                sectionName: "Crew",
                sectionId: "crewOther",
                shouldDisplay: (flight) => flight.flightCategory === "Other",
                inputs: [
                    {
                        name: "PIC",
                        key: "pic",
                        type: "crew",
                        required: true
                    },
                    {
                        name: "SIC",
                        key: "sic",
                        type: "crew",
                    },
                    {
                        name: "Dead Head Crew",
                        key: "deadheadCrew",
                        type: "crew"
                    }
                ]
            },
            {
                sectionName: "Passengers",
                sectionId: "passengersAoc",
                shouldDisplay: (flight) => flight.flightCategory === "AOC",
                inputs: [
                    {
                        name: "Passengers",
                        key: "passengers",
                        type: "passengers",
                        requiredPassengerFields: ["lastName", "weight"]
                    },
                ]
            },
            {
                sectionName: "Passengers",
                sectionId: "passengersOther",
                shouldDisplay: (flight) => flight.flightCategory === "Other",
                inputs: [
                    {
                        name: "Passengers",
                        key: "passengers",
                        type: "passengers",
                        requiredPassengerFields: ["lastName", "weight"]
                    },
                    {
                        name: "Is Cost Sharing flight",
                        key: "isCostSharing",
                        type: "boolean",
                        required: true
                    }
                ]
            },
            {
                sectionName: "Route",
                sectionId: "route",
                shouldDisplay: (flight) => flight.aircraft !== undefined && flight.customRoute === undefined,
                inputs: [
                    {
                        parentEntity: "Flight",
                        name: "Route",
                        key: "route",
                        type: "route",
                        required: true,
                        //VFR flights fly VFR routes. Unfiltered, this offered IFR/Y/Z routes too, and
                        //picking one attached a validated IFR routing to a flight filed as VFR.
                        //(IFR/mixed flights use the IFR planner's own picker, which filters by rules.)
                        routeFilter: (r) => r.isIfr !== true,
                        customRouteKey: "customRoute"
                    },
                    {
                        name: "Alternate 1",
                        key: "altn1",
                        //Locked while an IFR flight is on a saved route: that route was validated
                        //against these values. "Use as custom route" detaches and unlocks them.
                        disabledWhen: (f: Flight) => f.route !== undefined && f.flightRules !== "V",
                        type: "text",
                        uppercase: true,
                        validateRules: {
                            validate: {
                                requiredForIfr: (value: any, formValues: any) =>
                                    (formValues.flightRules === "V" || (value && (value as string).length > 0))
                                        ? true
                                        : "An alternate aerodrome (ALTN) is required for IFR flight plans"
                            }
                        }
                    },

                    {
                        name: "Alternate 2",
                        key: "altn2",
                        //Locked while an IFR flight is on a saved route: that route was validated
                        //against these values. "Use as custom route" detaches and unlocks them.
                        disabledWhen: (f: Flight) => f.route !== undefined && f.flightRules !== "V",
                        type: "text",
                        uppercase: true
                    },
                    {
                        name: "IFR Route (Field 15)",
                        key: "ifpsRoute",
                        type: "ifrRoutePlanner"
                    }
                ]
            },

            {
                sectionName: "Custom Route",
                sectionId: "customRoute",
                shouldDisplay: (flight) => flight.aircraft !== undefined && flight.customRoute !== undefined,
                inputs: [
                    {
                        name: "DEP",
                        key: "customRoute.departure",
                        //Locked while an IFR flight is on a saved route: that route was validated
                        //against these values. "Use as custom route" detaches and unlocks them.
                        disabledWhen: (f: Flight) => f.route !== undefined && f.flightRules !== "V",
                        type: "flightPlanPoint",
                        required: true
                    },
                    {
                        name: "DEST",
                        key: "customRoute.destination",
                        //Locked while an IFR flight is on a saved route: that route was validated
                        //against these values. "Use as custom route" detaches and unlocks them.
                        disabledWhen: (f: Flight) => f.route !== undefined && f.flightRules !== "V",
                        type: "flightPlanPoint",
                        required: true
                    },
                    {
                        name: "Route",
                        key: "customRoute.waypoints",
                        intermediateStopsKey: "customRoute.intermediateStops",
                        type: "customRoute"
                    },
                    {
                        name: "Alternate 1",
                        key: "altn1",
                        //Locked while an IFR flight is on a saved route: that route was validated
                        //against these values. "Use as custom route" detaches and unlocks them.
                        disabledWhen: (f: Flight) => f.route !== undefined && f.flightRules !== "V",
                        type: "text",
                        uppercase: true,
                        validateRules: {
                            validate: {
                                requiredForIfr: (value: any, formValues: any) =>
                                    (formValues.flightRules === "V" || (value && (value as string).length > 0))
                                        ? true
                                        : "An alternate aerodrome (ALTN) is required for IFR flight plans"
                            }
                        }
                    },
                    {
                        name: "Alternate 2",
                        key: "altn2",
                        //Locked while an IFR flight is on a saved route: that route was validated
                        //against these values. "Use as custom route" detaches and unlocks them.
                        disabledWhen: (f: Flight) => f.route !== undefined && f.flightRules !== "V",
                        type: "text",
                        uppercase: true
                    },
                    {
                        name: "IFR Route (Field 15)",
                        key: "ifpsRoute",
                        type: "ifrRoutePlanner"
                    },
                ]
            },

            {
                sectionName: "Intermediate Stops",
                sectionId: "intermediateStops",
                shouldDisplay: (flight) => flight.aircraft !== undefined && ((flight.customRoute && flight.customRoute?.intermediateStops?.length > 0) || (flight.route !== undefined && flight.route?.intermediateStops?.length > 0)),
                inputs: [
                    {
                        name: "Passengers' Leg",
                        key: "passengersLeg",
                        type: "passengersLeg",
                    }
                ]
            },

            {
                sectionName: "Flight Info",
                sectionId: "flightInfo",
                shouldDisplay: (flight) => flight.aircraft !== undefined && (flight.route !== undefined || flight.customRoute !== undefined),
                inputs: [
                    {
                        name: "Flight Rules",
                        key: "flightRules",
                        type: "dropdown",
                        options: [
                            { value: "V", label: "(V) VFR" },
                            { value: "I", label: "(I) IFR" },
                            { value: "Y", label: "(Y) IFR->VFR" },
                            { value: "Z", label: "(Z) VFR->IFR" },
                        ],
                        required: true
                    },
                    {
                        name: "Type of Flight",
                        key: "flightType",
                        type: "dropdown",
                        options: [
                            { value: "S", label: "(S) Scheduled" },
                            { value: "N", label: "(N) Non-Scheduled" },
                            { value: "G", label: "(G) General" },
                            { value: "M", label: "(M) Military" },
                            { value: "X", label: "(X) Other" },
                        ],
                        required: true
                    },
                    {
                        name: "Cruising Speed",
                        key: "cruiseSpeed",
                        type: "dropdownInput",
                        options: [
                            { value: "N", label: "(N) Knots" },
                            { value: "K", label: "(K) Km/h" },
                        ],
                        required: true
                    },
                    {
                        name: "Cruising Level",
                        key: "cruiseLevel",
                        //Locked while an IFR flight is on a saved route: that route was validated
                        //against these values. "Use as custom route" detaches and unlocks them.
                        disabledWhen: (f: Flight) => f.route !== undefined && f.flightRules !== "V",
                        type: "dropdownInput",
                        options: [
                            { value: "VFR", label: "(V) VFR", hardcodedInputValue: "VFR" },
                            { value: "F", label: "(F) Fl.Lvl (100ft)" },
                            { value: "A", label: "(A) Alt. (100ft)" },
                            { value: "S", label: "(S) Std. Lvl (Dm)" },
                            { value: "M", label: "(M) Metric (Dm)" },
                        ],
                        required: true
                    },
                    {
                        name: "Total EET (mins)",
                        key: "totalEETMins",
                        type: "time-autoset",
                        getCalculatedValue: (flight) => {
                            if (!flight.cruiseSpeed || flight.cruiseSpeed.value === 0 ){
                                return 0
                            }
                            var mins: number
                            var distanceNM: number
                            //IFR flies the Field-15 airway route, which has no waypoints for a great-circle
                            //calc — use the autorouter-computed distance stored when the route was planned.
                            if (flight.flightRules !== "V" && flight.ifrDistanceNM && flight.ifrDistanceNM > 0) {
                                distanceNM = flight.ifrDistanceNM
                            } else if (flight.route) {
                                distanceNM = flight.route.distanceNM
                            } else if (flight.customRoute) {
                                if (flight.customRoute.departure === undefined || flight.customRoute.destination === undefined) {
                                    return 0
                                }
                                distanceNM = calculateDistanceBetweenRoutePoints(
                                    getRoutePointFromFlightPlanPoint(flight.customRoute.departure),
                                    getRoutePointFromFlightPlanPoint(flight.customRoute.destination),
                                    flight.customRoute.waypoints.map((p) => getRoutePointFromFlightPlanZZZZPoint(p)),
                                )
                            } else {
                                return 0
                            }

                            if (flight.cruiseSpeed.unit === "N") {
                                mins = distanceNM / (flight.cruiseSpeed.value / 60)
                            } else {
                                mins = distanceNM / ((flight.cruiseSpeed.value * 0.539) / 60)
                            }
                            return Math.ceil(Math.round(mins) / 5) * 5
                        },
                        required: true,
                        validateRules: {
                            min: {
                                value: 5,
                                message: "Total EET must be at least 5 mins"
                            }
                        }
                    }
                ]
            },

            {
                sectionName: "Fuel Planning",
                sectionId: "fuelPlanning",
                shouldDisplay: (flight) => flight.aircraft !== undefined && (flight.route !== undefined || flight.customRoute !== undefined),
                inputs: [
                    {
                        name: "Fuel on Board",
                        key: "fuelOnBoard",
                        type: "fuelOnBoard",
                    },
                    {
                        name: "Endurance (mins)",
                        key: "enduranceMins",
                        type: "time-autoset",
                        getCalculatedValue: (flight) => { 
                            if (!flight.aircraft || !flight.fuelOnBoard){
                                return 0
                            }
                            const totalFuelInFuelUnit = flight.fuelOnBoard.reduce((prev, curr) => prev + curr.fuel, 0)
                            const fuelPerHour = flight.aircraft.fuelPerHour
                            const perMin = fuelPerHour / 60
                            const totalMins = totalFuelInFuelUnit / perMin

                            //Round up to next 5 mins
                            return Math.ceil(Math.round(totalMins)/5)*5
                        },
                        required: true,
                        validateRules: {
                            min: {
                                value: 5,
                                message: "Endurance must be at least 5 mins"
                            }
                        }
                    }
                ]
            },

            {
                sectionName: "Flight Plan",
                sectionId: "flightPlan",
                shouldDisplay: (flight) => flight.aircraft !== undefined,
                inputs: [
                    {
                        name: "SAR Equipment",
                        key: "sarEquipment",
                        type: "aircraftSar",
                        required: true
                    },
                    {
                        name: "Equipment Codes",
                        key: "equipmentCodes",
                        type: "text",
                        uppercase: true,
                        required: true,
                        validateRules: {
                            pattern: {
                                value: /^((A|B|C|D|E1|E2|E3|F|G|H|I|J1|J2|J3|J4|J5|J6|J7|K|L|M1|M2|M3|O|P1|P2|P3|P4|P5|P6|P7|P8|P9|R|S|T|U|V|W|X|Y|Z)+)$|^N$/,
                                message: "Invalid equipment code"
                            }
                        }
                    },
                    {
                        name: "Surveillance Equipment Codes",
                        key: "surveillanceEquipmentCodes",
                        type: "text",
                        uppercase: true,
                        required: true,
                        validateRules: {
                            pattern: {
                                value: /^((A|C|E|H|I|L|P|S|X|B1|B2|U1|U2|V1|V2|D1|G1)+)$|^N$/,
                                message: "Invalid surveillance equipment code"
                            }
                        }
                    },
                    {
                        name: "Operator Name",
                        key: "operatorName",
                        type: "text",
                        uppercase: true,
                        required: false
                    },

                    {
                        name: "Persons on board",
                        key: "personsOnBoard",
                        type: "pob",
                        required: true
                    },
                    {
                        name: "Field 18 Remarks RMK/",
                        key: "field18Remarks",
                        type: "text",
                        uppercase: true,
                        multiline: true,
                        validateRules: {
                            pattern: {
                                value: /^([0-9]|[ '()+,=?./]|[A-Z])+$/,
                                message: "Invalid characters. Supported characters are 0-9, A-Z, space, '()+,=?./"
                            }
                        }
                    }
                ]
            },

            {
                sectionName: "IFR Details",
                sectionId: "ifr",
                shouldDisplay: (flight) => flight.aircraft !== undefined && flight.flightRules !== "V",
                //Pre-filled from the aircraft when one is picked (EntityAircraftPicker copies pbn /
                //field18Nav-Com-Dat / fuelPerHour / MTOW), so this is only opened to deviate from the
                //aircraft for this one flight. Folded away until then.
                collapsedByDefault: true,
                inputs: [
                    {
                        name: "PBN (PBN/) - auto-filled from aircraft",
                        key: "pbn",
                        type: "text",
                        uppercase: true,
                        validateRules: {
                            pattern: {
                                value: /^[A-Z0-9]*$/,
                                message: "PBN must contain only letters and digits, e.g. B2D2"
                            }
                        }
                    },
                    {
                        name: "Navigation Equipment (NAV/)",
                        key: "field18Nav",
                        type: "text",
                        uppercase: true
                    },
                    {
                        name: "Communication Equipment (COM/)",
                        key: "field18Com",
                        type: "text",
                        uppercase: true
                    },
                    {
                        name: "Data Link (DAT/)",
                        key: "field18Dat",
                        type: "text",
                        uppercase: true
                    },
                    {
                        name: "Fuel burn per hour" + (fuelUnit ? ` (${fuelUnit})` : ""),
                        key: "ifrFuelPerHour",
                        type: "number",
                        validateRules: {
                            validate: {
                                requiredForIfr: (value: any, formValues: any) =>
                                    (formValues.flightRules === "V" || (value !== undefined && value !== null && Number(value) > 0))
                                        ? true
                                        : "Enter fuel burn — required for IFR route-finding"
                            }
                        }
                    },
                    {
                        name: "Max take-off mass (MTOW, kg)",
                        key: "ifrMaxMass",
                        type: "number",
                        validateRules: {
                            validate: {
                                requiredForIfr: (value: any, formValues: any) =>
                                    (formValues.flightRules === "V" || (value !== undefined && value !== null && Number(value) > 0))
                                        ? true
                                        : "Enter MTOW — required for IFR route-finding"
                            }
                        }
                    }
                ]
            },

            {
                sectionName: "AFTN Addresses",
                sectionId: "aftn",
                //Hidden while a saved route is attached: filing then uses that route's own addresses
                //(getAftnAddressesForFlightBasedOnRouteType reads flight.route first), so editing the
                //customRoute copy here would have no effect.
                shouldDisplay: (flight) => flight.customRoute !== undefined && flight.route === undefined,
                inputs: [
                    {
                        name: "AFTN Addresses",
                        key: "customRoute.aftnAddresses",
                        type: "aftnAddresses"
                    }
                ]
            },

            {
                sectionName: "External Boarding Pass",
                sectionId: "boardingPass",
                shouldDisplay: (flight) => store.getState().organizations.selectedOrganization?.externalWalletPass !== undefined ,
                inputs: [
                    {
                        name: store.getState().organizations.selectedOrganization?.externalWalletPass?.fieldDisplayName ?? "",
                        key: "walletPassExternalId",
                        type: "text",
                        validateRules: {
                            pattern: {
                                value: RegExp(store.getState().organizations.selectedOrganization?.externalWalletPass?.formatRegex ?? ""),
                                message: store.getState().organizations.selectedOrganization?.externalWalletPass?.formatDisplayError ?? ""
                            }   
                        }
                    }
                ]
            },

            {
                sectionName: "Weight & Balance",
                sectionId: "wb",
                shouldDisplay: (flight) => flight.aircraft !== undefined,
                inputs: [
                    {
                        name: "Check W&B",
                        key: "wb",
                        type: "wb",
                    }
                ]
            }
        ]
    }

    if (rulesMode === "ifr") {
        //IFR flow: rules restricted to the IFR family. Flight Info sits AFTER the Route section (the
        //route planner is the first thing after DEP/DEST). The "VFR" cruise-level option STAYS
        //available — Z (VFR first) files a VFR initial level; validators reject it for I and Y.
        const flightInfoSection = info.sections.find((s) => s.sectionName === "Flight Info")
        const rulesInput = flightInfoSection?.inputs.find((i) => "key" in i && i.key === "flightRules")
        if (rulesInput && "options" in rulesInput) {
            rulesInput.options = rulesInput.options.filter((o) => o.value !== "V")
        }

        const pullSection = (name: string) => {
            const idx = info.sections.findIndex((s) => s.sectionName === name)
            return idx >= 0 ? info.sections.splice(idx, 1)[0] : undefined
        }
        //Only Flight Info moves up — the route planner needs the rules/level right after DEP/DEST.
        //IFR Details stays in its natural place further down (after Flight Plan): it is pre-filled from
        //the aircraft and rarely touched, so it does not belong between the route and the flight fields.
        const movedSections = [pullSection("Flight Info")]
            .filter((s): s is NonNullable<typeof s> => s !== undefined)
        movedSections.forEach((s) => { s.shouldDisplay = (flight) => flight.aircraft !== undefined })
        //Flight Info sits AFTER the route section. IFR flights use the customRoute section (DEP/DEST +
        //IFR planner, renamed "Route" below) — anchor to it, not the hidden saved-route "route"
        //section, or the moved section would land before the visible route.
        const routeIdx = info.sections.findIndex((s) => s.sectionId === "customRoute")
        info.sections.splice(routeIdx >= 0 ? routeIdx + 1 : info.sections.length, 0, ...movedSections)

        //Clean state (no route yet, customRoute === undefined): the "route" section is the always-visible
        //entry point. Keep ONLY the IFR planner there — its route selector box + "Custom Route" button.
        //The VFR saved-route picker and the alternates belong to the DEP/DEST step (customRoute section),
        //which appears once the user picks a saved route or enters custom mode. Mirrors the VFR flow.
        const savedRouteSection = info.sections.find((s) => s.sectionId === "route")
        if (savedRouteSection) {
            savedRouteSection.inputs = savedRouteSection.inputs.filter((i) => "type" in i && i.type === "ifrRoutePlanner")
        }

        //Custom-route mode (customRoute defined): the classic layout — DEP/DEST and alternates first,
        //then the IFR planner (Plan / Re-plan) at the BOTTOM. Only the VFR waypoint editor is dropped
        //(IFR flies the validated Field 15). The route selector lives in the clean "route" section above.
        const customRouteSection = info.sections.find((s) => s.sectionId === "customRoute")
        if (customRouteSection) {
            let inputs = customRouteSection.inputs.filter((i) => !("key" in i && i.key === "customRoute.waypoints"))
            //Mirror the Cruising Level field (it also lives in Flight Info below) into the Route section,
            //so the flight level the IFR planner needs can be set right next to the route. Same key → the
            //two instances stay in sync. A shallow clone keeps their per-field lock state independent.
            //Placed just before the planner (which sits at the bottom, after DEP/DEST/alternates).
            const cruiseLevelInput = flightInfoSection?.inputs.find((i) => "key" in i && i.key === "cruiseLevel")
            if (cruiseLevelInput) {
                const plannerIdx = inputs.findIndex((i) => "type" in i && i.type === "ifrRoutePlanner")
                const clonedLevel = { ...cruiseLevelInput }
                if (plannerIdx >= 0) inputs.splice(plannerIdx, 0, clonedLevel)
                else inputs.push(clonedLevel)
            }
            customRouteSection.inputs = inputs
            customRouteSection.sectionName = "Route"
        }
        const intStopsSection = info.sections.find((s) => s.sectionId === "intermediateStops")
        if (intStopsSection) {
            intStopsSection.shouldDisplay = () => false
        }
        //Pure IFR (I) is addressed to the two EUROCONTROL IFPS units automatically — no AFTN address
        //finding. Mixed-rules (Y/Z) flights keep the section: their VFR segments' ATS units are not
        //distributed by IFPS and must be addressed directly (merged with the IFPS pair at filing).
        //The AFTN addresses depend on the route's VFR points, so the section only appears once a route
        //is selected (there is nothing to find AFTNs for beforehand).
        const aftnSection = info.sections.find((s) => s.sectionId === "aftn")
        if (aftnSection) {
            aftnSection.shouldDisplay = (flight) =>
                (flight.flightRules === "Y" || flight.flightRules === "Z") && !!flight.ifpsRoute
        }
    } else if (rulesMode === "vfr") {
        //VFR flow: rules are locked to "V" at creation — hide the dropdown entirely.
        //(All IFR sections/validators already self-hide when flightRules === "V".)
        const flightInfoSection = info.sections.find((s) => s.sectionName === "Flight Info")
        if (flightInfoSection) {
            flightInfoSection.inputs = flightInfoSection.inputs.filter((i) => !("key" in i && i.key === "flightRules"))
        }
    }

    return info
}

export function getDefaultDateForFlightDateTimePicker() {
    const now = new Date();
    const d = new Date(now.getTime() + 25 * 60 * 1000);
    d.setMinutes(Math.ceil(d.getMinutes() / 5) * 5);
    return d;
}

//Post-flight student training record: Dual is filled by the PIC (instructor), Solo by the solo supervisor
export function flightNeedsTrainingRecord(flight: Flight, organization: Organization): boolean {
    return organization.trainingRecord?.enabled === true && flight.flightCategory === "Training" && (flight.trainingFlightCategory === "Dual" || flight.trainingFlightCategory === "Solo")
}

export function getTrainingRecordInstructor(flight: Flight): Crew | undefined {
    if (flight.trainingFlightCategory === "Dual") return flight.pic
    if (flight.trainingFlightCategory === "Solo") return flight.soloSupervisor
    return undefined
}

export function flightIsWaitingForRiskAssessment(flight: Flight, organization: Organization): boolean {
    if (organization.riskAssessment.enabled && organization.riskAssessment.disableFplFlightCategories.includes(flight.flightCategory)) {
        if (flight.customDocs?.riskAssessment === undefined) {
            return true
        } else if (flight.customDocs?.riskAssessment.level.requestSignFrom !== undefined && flight.customDocs?.riskAssessmentSigned === undefined) {
            return true
        }
    }

    return false
}